import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Plus, Trash2, AlertTriangle, Handshake, ArrowRight, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import Reveal from "@/components/site/Reveal";
import GuestDetailsDialog from "@/components/site/GuestDetailsDialog";
import { getGuest } from "@/lib/guest";
import { Switch } from "@/components/ui/switch";
import { trackEvent } from "@/lib/settings";
import { usePageContent } from "@/lib/content";
import { mergeRC } from "@/lib/revenueContent";
import { mergePM } from "@/lib/partnershipContent";
import PartnershipCompare from "@/components/site/PartnershipCompare";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const num = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const N = (v) => (v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v));

const TYPES = [
  { id: "villa_event", label: "Villa + Event Space", desc: "A private villa with dedicated celebration spaces." },
  { id: "rooms_event", label: "Rooms + Event Space", desc: "Guest rooms alongside dedicated celebration spaces." },
  { id: "villa_rooms_event", label: "Villa + Rooms + Event Space", desc: "The full estate — villa, rooms and celebration spaces." },
];

function Num({ label, value, onChange, prefix, suffix, testId, hint, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">{label}</span>
      <div className="mt-1.5 flex items-center gap-2 border border-ink/15 bg-white px-3 py-2.5 focus-within:border-ink transition-colors">
        {prefix && <span className="text-sm text-graphite shrink-0">{prefix}</span>}
        <input
          type="number" min="0" value={value} placeholder={placeholder || "0"}
          onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
          className="w-full bg-transparent outline-none text-sm"
          data-testid={testId}
        />
        {suffix && <span className="text-[11px] text-graphite whitespace-nowrap shrink-0">{suffix}</span>}
      </div>
      {hint && <span className="text-[11px] text-graphite/70 mt-1 block">{hint}</span>}
    </label>
  );
}

function Step({ n, title, intro, children }) {
  return (
    <Reveal>
      <section className="border-t border-ink/10 pt-8 pb-10">
        <div className="flex items-baseline gap-4">
          <span className="font-display text-2xl text-clay">{String(n).padStart(2, "0")}</span>
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-light">{title}</h2>
            {intro && <p className="text-sm text-graphite mt-1.5 max-w-xl leading-relaxed">{intro}</p>}
          </div>
        </div>
        <div className="mt-6">{children}</div>
      </section>
    </Reveal>
  );
}

const SumRow = ({ label, value, bold, muted, testId }) => (
  <div className={`flex items-baseline justify-between gap-3 ${muted ? "text-ivory/50 text-xs" : "text-sm"} ${bold ? "font-medium" : ""}`}>
    <span className="leading-snug">{label}</span>
    <span className="whitespace-nowrap tabular-nums" data-testid={testId}>{value}</span>
  </div>
);

function CalculatorBody({ cfg }) {
  const { content: pmContent } = usePageContent("partnership_models");
  const pmCfg = useMemo(() => mergePM(pmContent), [pmContent]);
  const stageCfg = pmCfg.stages || {};
  const stageOptions = stageCfg.enabled !== false ? stageCfg.options || [] : [];
  const stepCfg = (id) => cfg.steps.find((s) => s.id === id) || {};
  const capCfg = stepCfg("capacity");
  const socCfg = stepCfg("socials");

  const [ptype, setPtype] = useState("");
  const [stage, setStage] = useState("");
  const [propertyName, setPropertyName] = useState("");
  const [propSize, setPropSize] = useState("");
  const [propUnit, setPropUnit] = useState("acres");
  const [eventSqft, setEventSqft] = useState("");
  const [pools, setPools] = useState("");
  const [rooms, setRooms] = useState("");
  const [bedGuests, setBedGuests] = useState("");
  const [cotGuests, setCotGuests] = useState("");
  const [bedPrice, setBedPrice] = useState(N(capCfg.bed_price) || 5000);
  const [cotPrice, setCotPrice] = useState(N(capCfg.cot_price) || 3500);
  const [spaces, setSpaces] = useState([]);
  const [targetPeople, setTargetPeople] = useState(null);
  const [targetDraft, setTargetDraft] = useState(null);
  const [roomRate, setRoomRate] = useState("");
  const [extras, setExtras] = useState([]);
  const [weddings, setWeddings] = useState("");
  const [withFood, setWithFood] = useState(true);
  const [withStay, setWithStay] = useState(false);
  const [foodPerHead, setFoodPerHead] = useState(N(socCfg.food_per_head) || 1500);
  const [guestOpen, setGuestOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const on = (id) => {
    const s = cfg.steps.find((x) => x.id === id);
    return !!s && s.enabled !== false;
  };
  const spacesOn = on("spaces");
  const leisureOn = on("leisure");
  const weddingsOn = on("weddings");
  const socialsOn = on("socials") && spacesOn;

  const setSpaceCount = (n) => {
    const count = Math.min(10, Math.max(0, N(n)));
    setSpaces((prev) => {
      const next = [...prev];
      while (next.length < count) next.push({ name: `Event space ${next.length + 1}`, capacity: "", cost: "", events: "" });
      return next.slice(0, count);
    });
  };
  const setSpace = (i, k, v) => setSpaces((prev) => prev.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const calc = useMemo(() => {
    const spacesEff = spacesOn ? spaces : [];
    const capacity = N(bedGuests) + N(cotGuests);
    const dailyRevenue = N(bedGuests) * N(bedPrice) + N(cotGuests) * N(cotPrice);
    const avgTicket = capacity ? dailyRevenue / capacity : 0;
    const potentialPeople = capacity * 365;
    const potentialRevenue = potentialPeople * avgTicket;
    const minPeople = capacity * 52;
    const minRevenue = minPeople * avgTicket;
    const target = Math.min(potentialPeople, Math.max(minPeople, targetPeople == null ? minPeople : N(targetPeople)));
    const leisureRevenue = leisureOn ? target * avgTicket : 0;
    const leisureDays = leisureOn && capacity ? target / capacity : 0;

    const spacesCost = spacesEff.reduce((a, s) => a + N(s.cost), 0);
    const spacesCapacity = spacesEff.reduce((a, s) => a + N(s.capacity), 0);
    const extrasTotal = extras.reduce((a, x) => a + N(x.amount), 0);
    const venueCost = N(roomRate) * N(rooms) + spacesCost + extrasTotal;
    const weddingRevenue = weddingsOn ? venueCost * N(weddings) : 0;

    const socialsEvents = socialsOn ? spacesEff.reduce((a, s) => a + N(s.events), 0) : 0;
    const socialsVenueRev = socialsOn ? spacesEff.reduce((a, s) => a + N(s.events) * N(s.cost), 0) : 0;
    const socialsFoodRev = socialsOn && withFood ? spacesEff.reduce((a, s) => a + N(s.events) * N(s.capacity) * N(foodPerHead), 0) : 0;
    const socialsStayRev = socialsOn && withStay ? socialsEvents * N(rooms) * N(roomRate) : 0;
    const socialsPeople = socialsOn ? spacesEff.reduce((a, s) => a + N(s.events) * N(s.capacity), 0) : 0;
    const socialsRevenue = socialsVenueRev + socialsFoodRev + socialsStayRev;
    const socialsDays = socialsEvents;

    const total = leisureRevenue + weddingRevenue + socialsRevenue;
    const daysCommitted = leisureDays + (weddingsOn ? N(weddings) : 0) + socialsDays;
    return {
      capacity, dailyRevenue, avgTicket, potentialPeople, potentialRevenue, minPeople, minRevenue,
      target, leisureRevenue, leisureDays, spacesCost, spacesCapacity, venueCost, weddingRevenue,
      socialsEvents, socialsVenueRev, socialsFoodRev, socialsStayRev,
      socialsDays, socialsPeople, socialsRevenue, total, daysCommitted,
    };
  }, [bedGuests, cotGuests, bedPrice, cotPrice, targetPeople, spaces, extras, roomRate, rooms, weddings, withFood, withStay, foodPerHead, spacesOn, leisureOn, weddingsOn, socialsOn]);

  const downloadPdf = async (guest) => {
    setDownloading(true);
    try {
      const payload = {
        ...guest,
        property_name: propertyName, property_type: ptype,
        property_stage: (stageOptions.find((s) => s.id === stage) || {}).label || "",
        property_size: N(propSize), property_size_unit: propUnit,
        event_area_sqft: N(eventSqft), pools: N(pools),
        rooms: N(rooms), bed_guests: N(bedGuests), cot_guests: N(cotGuests),
        bed_price: N(bedPrice), cot_price: N(cotPrice),
        event_spaces: (spacesOn ? spaces : []).map((s) => ({ name: s.name, capacity: N(s.capacity), cost: N(s.cost), events: socialsOn ? N(s.events) : 0 })),
        capacity: calc.capacity, avg_ticket: calc.avgTicket, potential_revenue: calc.potentialRevenue,
        min_people: calc.minPeople, min_revenue: calc.minRevenue,
        target_people: calc.target, leisure_days: calc.leisureDays, leisure_revenue: calc.leisureRevenue,
        room_rate: N(roomRate), extras: extras.map((x) => ({ label: x.label, amount: N(x.amount) })),
        venue_cost: calc.venueCost, weddings: weddingsOn ? N(weddings) : 0, wedding_revenue: calc.weddingRevenue,
        socials_events: calc.socialsEvents, socials_people: calc.socialsPeople,
        with_food: withFood, food_per_head: N(foodPerHead),
        with_stay: withStay, socials_venue_rev: calc.socialsVenueRev,
        socials_food_rev: calc.socialsFoodRev, socials_stay_rev: calc.socialsStayRev,
        socials_days: calc.socialsDays, socials_revenue: calc.socialsRevenue,
        total_revenue: calc.total, days_committed: calc.daysCommitted,
      };
      const r = await api.post("/public/revenue-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Happy-Retreats-Revenue-Projection.pdf";
      a.click();
      URL.revokeObjectURL(url);
      trackEvent("revenue_projection_pdf", { property: propertyName, total: Math.round(calc.total) });
      toast.success("Your projection is downloading");
    } catch {
      toast.error("Could not prepare the PDF — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const requestDownload = () => {
    if (!ptype || !calc.capacity) { toast.error("Choose your property type and add sleeping capacity first"); return; }
    if (stageOptions.length > 0 && !stage) { toast.error("Select the stage of the property in step 1"); return; }
    if (!N(propSize) || eventSqft === "" || pools === "") { toast.error("Please fill the property size, event-space area and pools (enter 0 if none) in step 1"); return; }
    const g = getGuest();
    if (g) downloadPdf(g);
    else setGuestOpen(true);
  };

  const overbooked = calc.daysCommitted > 365;
  const daysPct = Math.min(100, (calc.daysCommitted / 365) * 100);

  const renderStep = (s, n) => {
    if (s.id === "capacity") {
      return (
        <Step key={s.id} n={n} title={s.title} intro={s.intro}>
          <div className="grid sm:grid-cols-3 gap-4">
            <Num label="Rooms available" value={rooms} onChange={setRooms} testId="rc-rooms" />
            <Num label="Guests on beds" value={bedGuests} onChange={setBedGuests} testId="rc-bed-guests" />
            <Num label="Guests on extra cots" value={cotGuests} onChange={setCotGuests} testId="rc-cot-guests" />
            <Num label="Price per head — beds" prefix="₹" suffix="/night" value={bedPrice} onChange={setBedPrice} testId="rc-bed-price" />
            <Num label="Price per head — extra cots" prefix="₹" suffix="/night" value={cotPrice} onChange={setCotPrice} testId="rc-cot-price" />
          </div>
          {calc.capacity > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="rc-capacity-chips">
              <span className="text-xs bg-bone px-3 py-1.5">Nightly capacity: <strong>{num(calc.capacity)} guests</strong></span>
              <span className="text-xs bg-bone px-3 py-1.5">Full-house night: <strong>{inr(calc.dailyRevenue)}</strong></span>
              <span className="text-xs bg-bone px-3 py-1.5 border border-clay/40">Average ticket per head: <strong data-testid="rc-avg-ticket">{inr(calc.avgTicket)}</strong></span>
            </div>
          )}
        </Step>
      );
    }
    if (s.id === "spaces") {
      return (
        <Step key={s.id} n={n} title={s.title} intro={s.intro}>
          <div className="max-w-[200px]">
            <Num label="How many event spaces?" value={spaces.length || ""} onChange={setSpaceCount} testId="rc-space-count" />
          </div>
          {spaces.map((sp, i) => (
            <div key={i} className="mt-4 grid sm:grid-cols-3 gap-3 border border-ink/10 bg-white/60 p-4">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Space name</span>
                <input value={sp.name} onChange={(e) => setSpace(i, "name", e.target.value)}
                  className="mt-1.5 w-full border border-ink/15 bg-white px-3 py-2.5 outline-none text-sm focus:border-ink" data-testid={`rc-space-name-${i}`} />
              </label>
              <Num label="Capacity (pax)" value={sp.capacity} onChange={(v) => setSpace(i, "capacity", v)} testId={`rc-space-cap-${i}`} />
              <Num label="Venue price" prefix="₹" suffix="/day" value={sp.cost} onChange={(v) => setSpace(i, "cost", v)} testId={`rc-space-cost-${i}`} />
            </div>
          ))}
        </Step>
      );
    }
    if (s.id === "leisure") {
      return (
        <Step key={s.id} n={n} title={s.title} intro={s.intro}>
          <p className="text-sm text-graphite -mt-2 mb-5">
            At full capacity you could host <strong>{num(calc.potentialPeople)}</strong> paying guests a year ({num(calc.capacity)} × 365 days).
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="border border-ink/10 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Total annual potential</div>
              <div className="font-display text-2xl mt-2" data-testid="rc-potential-revenue">{inr(calc.potentialRevenue)}</div>
              <div className="text-xs text-graphite mt-1">{num(calc.potentialPeople)} guests × {inr(calc.avgTicket)} avg ticket</div>
            </div>
            <div className="border border-clay/50 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-clay font-medium">Minimum guarantee — 52 weekends</div>
              <div className="font-display text-2xl mt-2" data-testid="rc-min-revenue">{inr(calc.minRevenue)}</div>
              <div className="text-xs text-graphite mt-1">{num(calc.minPeople)} guests × {inr(calc.avgTicket)} avg ticket</div>
            </div>
          </div>
          <div className="mt-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Your target — guests per year</span>
              <span className="text-sm font-medium" data-testid="rc-target-value">{num(calc.target)} guests · ~{num(calc.leisureDays)} days</span>
            </div>
            <input
              type="range" min={calc.minPeople} max={Math.max(calc.minPeople + 1, calc.potentialPeople)} step={1}
              value={calc.target} onChange={(e) => { setTargetPeople(Number(e.target.value)); setTargetDraft(null); }}
              className="mt-3 w-full accent-[#9E7B4F]" disabled={!calc.capacity} data-testid="rc-target-slider"
            />
            <div className="flex justify-between text-[11px] text-graphite mt-1">
              <span>Min guarantee · {num(calc.minPeople)}</span>
              <span>Full year · {num(calc.potentialPeople)}</span>
            </div>
            <div className="mt-4 flex items-end gap-3">
              <label className="block w-48">
                <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Or type an exact number</span>
                <input
                  type="number" min={calc.minPeople} max={calc.potentialPeople}
                  value={targetDraft ?? calc.target}
                  onChange={(e) => setTargetDraft(e.target.value)}
                  onBlur={() => { if (targetDraft !== null) { setTargetPeople(N(targetDraft)); setTargetDraft(null); } }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  disabled={!calc.capacity}
                  className="mt-1.5 w-full border border-ink/15 bg-white px-3 py-2.5 outline-none text-sm focus:border-ink transition-colors"
                  data-testid="rc-target-input"
                />
              </label>
              <span className="text-[11px] text-graphite/70 pb-3">guests · between {num(calc.minPeople)} and {num(calc.potentialPeople)}</span>
            </div>
            <div className="mt-4 bg-bone px-4 py-3 flex items-baseline justify-between">
              <span className="text-sm">Realistic leisure & corporate revenue</span>
              <span className="font-display text-xl" data-testid="rc-leisure-revenue">{inr(calc.leisureRevenue)}</span>
            </div>
          </div>
        </Step>
      );
    }
    if (s.id === "weddings") {
      return (
        <Step key={s.id} n={n} title={s.title} intro={s.intro}>
          <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
            <Num label="Room rate per night" prefix="₹" suffix={`× ${num(N(rooms))} rooms`} value={roomRate} onChange={setRoomRate} testId="rc-room-rate" />
            <Num label="Weddings targeted per year" value={weddings} onChange={setWeddings} testId="rc-weddings" />
          </div>
          <div className="mt-4">
            {extras.map((x, i) => (
              <div key={i} className="flex items-end gap-3 mt-3">
                <label className="block flex-1">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Additional charge</span>
                  <input value={x.label} onChange={(e) => setExtras((p) => p.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))}
                    placeholder="e.g. Power backup, cleaning" className="mt-1.5 w-full border border-ink/15 bg-white px-3 py-2.5 outline-none text-sm focus:border-ink" data-testid={`rc-extra-label-${i}`} />
                </label>
                <div className="w-40">
                  <Num label="Amount" prefix="₹" value={x.amount} onChange={(v) => setExtras((p) => p.map((y, j) => (j === i ? { ...y, amount: v } : y)))} testId={`rc-extra-amount-${i}`} />
                </div>
                <button type="button" onClick={() => setExtras((p) => p.filter((_, j) => j !== i))} className="p-2.5 border border-ink/15 text-graphite hover:text-red-600 hover:border-red-300" aria-label="Remove charge" data-testid={`rc-extra-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setExtras((p) => [...p, { label: "", amount: "" }])}
              className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-medium text-ink hover:text-clay transition-colors" data-testid="rc-extra-add">
              <Plus className="w-3.5 h-3.5" /> Add an additional charge
            </button>
          </div>
          <div className="mt-5 bg-bone px-4 py-3 space-y-1.5">
            <div className="flex justify-between text-xs text-graphite"><span>Rooms · {num(N(rooms))} × {inr(N(roomRate))}</span><span>{inr(N(roomRate) * N(rooms))}</span></div>
            <div className="flex justify-between text-xs text-graphite"><span>Event spaces</span><span>{inr(calc.spacesCost)}</span></div>
            {extras.length > 0 && <div className="flex justify-between text-xs text-graphite"><span>Additional charges</span><span>{inr(extras.reduce((a, x) => a + N(x.amount), 0))}</span></div>}
            <div className="flex items-baseline justify-between border-t border-ink/10 pt-2"><span className="text-sm">Venue cost per wedding</span><span className="font-display text-lg" data-testid="rc-venue-cost">{inr(calc.venueCost)}</span></div>
            <div className="flex items-baseline justify-between"><span className="text-sm">Wedding revenue · {num(N(weddings))} weddings</span><span className="font-display text-xl" data-testid="rc-wedding-revenue">{inr(calc.weddingRevenue)}</span></div>
          </div>
        </Step>
      );
    }
    if (s.id === "socials") {
      return (
        <Step key={s.id} n={n} title={s.title} intro={s.intro}>
          <p className="text-sm text-graphite -mt-2 mb-5">
            For each space, set how many private events you can realistically host in a year at its listed price.
          </p>
          <div className="max-w-2xl space-y-3">
            {spaces.map((sp, i) => (
              <div key={i} className="flex flex-wrap items-center gap-4 border border-ink/10 bg-white/60 p-4" data-testid={`rc-socials-space-${i}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm font-medium">{sp.name || `Event space ${i + 1}`}</div>
                  <div className="text-xs text-graphite mt-0.5">{num(N(sp.capacity))} pax · {inr(N(sp.cost))}/event</div>
                </div>
                <div className="w-44">
                  <Num label="Events per year" value={sp.events ?? ""} onChange={(v) => setSpace(i, "events", v)} testId={`rc-socials-events-${i}`} />
                </div>
                <div className="w-36 text-right">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Venue revenue</div>
                  <div className="font-display text-lg mt-1" data-testid={`rc-socials-space-rev-${i}`}>{inr(N(sp.events) * N(sp.cost))}</div>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between gap-4 border border-ink/10 bg-white px-4 py-3">
              <div>
                <div className="text-sm font-medium">Sold with food?</div>
                <div className="text-xs text-graphite mt-0.5">Adds space capacity × cost per plate, per event</div>
              </div>
              <Switch checked={withFood} onCheckedChange={setWithFood} data-testid="rc-withfood" />
            </div>
            {withFood && (
              <div className="max-w-[240px]">
                <Num label="Average cost per plate" prefix="₹" value={foodPerHead} onChange={setFoodPerHead} testId="rc-food-price" />
              </div>
            )}

            <div className="flex items-center justify-between gap-4 border border-ink/10 bg-white px-4 py-3">
              <div>
                <div className="text-sm font-medium">Sold with stay?</div>
                <div className="text-xs text-graphite mt-0.5">Adds all {num(N(rooms))} rooms × room rate per night, per event</div>
              </div>
              <Switch checked={withStay} onCheckedChange={setWithStay} data-testid="rc-withstay" />
            </div>
            {withStay && (
              <div className="max-w-[280px]">
                <Num label="Room rate per night" prefix="₹" suffix={`× ${num(N(rooms))} rooms`} value={roomRate} onChange={setRoomRate}
                  hint="Shared with the weddings section." testId="rc-socials-room-rate" />
              </div>
            )}

            <div className="bg-bone px-4 py-3 space-y-1.5">
              <div className="flex justify-between text-xs text-graphite"><span>Events per year · {num(calc.socialsEvents)} · guests reachable {num(calc.socialsPeople)}</span><span data-testid="rc-socials-venue-rev">{inr(calc.socialsVenueRev)}</span></div>
              {withFood && <div className="flex justify-between text-xs text-graphite"><span>Food · capacity × {inr(N(foodPerHead))}/plate × events</span><span data-testid="rc-socials-food-rev">{inr(calc.socialsFoodRev)}</span></div>}
              {withStay && <div className="flex justify-between text-xs text-graphite"><span>Stay · {num(N(rooms))} rooms × {inr(N(roomRate))} × {num(calc.socialsEvents)} events</span><span data-testid="rc-socials-stay-rev">{inr(calc.socialsStayRev)}</span></div>}
              <div className="flex items-baseline justify-between border-t border-ink/10 pt-2"><span className="text-sm">Private socials revenue</span><span className="font-display text-xl" data-testid="rc-socials-revenue">{inr(calc.socialsRevenue)}</span></div>
            </div>
          </div>
        </Step>
      );
    }
    return null;
  };

  const typeStep = stepCfg("type");
  const orderedSteps = cfg.steps.filter((s) => {
    if (s.id === "type") return false;
    if (s.enabled === false) return false;
    if (s.id === "socials" && (!spacesOn || spaces.length === 0)) return false;
    return true;
  });

  return (
    <div data-testid="revenue-calculator-page">
      <SEOHead
        title="Revenue Calculator — Project your property's earnings | Happy Retreats"
        description="A transparent, humanistic projection of your property's annual revenue potential across leisure stays, corporate retreats, weddings and private socials — the Happy Retreats way."
      />

      {/* Hero */}
      <section className="bg-ink text-ivory pt-44 md:pt-52 pb-14 md:pb-20">
        <div className="container-x">
          <div className="overline text-ivory/70 animate-fade-up">{cfg.hero.overline}</div>
          <h1 className="display-1 mt-8 max-w-4xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
            {cfg.hero.headline_pre}<span className="italic text-clay">{cfg.hero.headline_accent}</span>{cfg.hero.headline_post}
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-ivory/80 font-light leading-relaxed animate-fade-up" style={{ animationDelay: "0.3s" }}>
            {cfg.hero.subheadline}
          </p>
        </div>
      </section>

      <div className="container-x py-10 md:py-14">
        <div className="grid lg:grid-cols-[1fr,400px] gap-10 lg:gap-14 items-start">
          {/* Steps */}
          <div>
            <Step n={1} title={typeStep.title} intro={typeStep.intro}>
              <div className="grid sm:grid-cols-3 gap-3">
                {TYPES.map((t) => (
                  <button
                    key={t.id} type="button" onClick={() => setPtype(t.id)}
                    className={`text-left border p-4 transition-colors ${ptype === t.id ? "border-ink bg-ink text-ivory" : "border-ink/15 bg-white hover:border-ink/40"}`}
                    data-testid={`rc-type-${t.id}`}
                  >
                    <div className="text-sm font-medium">{t.label}</div>
                    <div className={`text-xs mt-1.5 leading-relaxed ${ptype === t.id ? "text-ivory/60" : "text-graphite"}`}>{t.desc}</div>
                  </button>
                ))}
              </div>
              {ptype && stageOptions.length > 0 && (
                <div className="mt-5" data-testid="rc-stage-picker">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium mb-2">{stageCfg.question || "What stage is the property at?"}</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {stageOptions.map((st) => (
                      <button
                        key={st.id} type="button" onClick={() => setStage(st.id)}
                        className={`text-left border p-4 transition-colors ${stage === st.id ? "border-ink bg-ink text-ivory" : "border-ink/15 bg-white hover:border-ink/40"}`}
                        data-testid={`rc-stage-${st.id}`}
                      >
                        <div className="text-sm font-medium">{st.label}</div>
                        <div className={`text-xs mt-1.5 leading-relaxed ${stage === st.id ? "text-ivory/60" : "text-graphite"}`}>{st.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 max-w-sm">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Property name (optional)</span>
                  <input value={propertyName} onChange={(e) => setPropertyName(e.target.value)} placeholder="e.g. Serene Meadows Estate"
                    className="mt-1.5 w-full border border-ink/15 bg-white px-3 py-2.5 outline-none text-sm focus:border-ink transition-colors" data-testid="rc-property-name" />
                </label>
              </div>
              {ptype && (
                <div className="mt-5 border border-ink/10 bg-white/60 p-4" data-testid="rc-property-details">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium mb-3">About the property — helps us plan operations & staffing</div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <label className="block">
                      <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Total property size</span>
                      <div className="mt-1.5 flex items-center gap-2 border border-ink/15 bg-white px-3 py-2 focus-within:border-ink transition-colors">
                        <input type="number" min="0" step="any" value={propSize} placeholder="0"
                          onChange={(e) => setPropSize(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
                          className="w-full bg-transparent outline-none text-sm" data-testid="rc-prop-size" />
                        <div className="flex shrink-0 border border-ink/15">
                          {["acres", "sqft"].map((u) => (
                            <button key={u} type="button" onClick={() => setPropUnit(u)}
                              className={`px-2 py-1 text-[10px] uppercase tracking-wide transition-colors ${propUnit === u ? "bg-ink text-ivory" : "bg-white text-graphite hover:text-ink"}`}
                              data-testid={`rc-prop-unit-${u}`}>{u}</button>
                          ))}
                        </div>
                      </div>
                    </label>
                    <Num label="Event spaces area" suffix="sqft" value={eventSqft} onChange={setEventSqft} testId="rc-event-sqft" />
                    <Num label="Number of pools" value={pools} onChange={setPools} hint="Enter 0 if there is no pool" testId="rc-pools" />
                  </div>
                </div>
              )}
            </Step>

            {ptype && orderedSteps.map((s, i) => renderStep(s, i + 2))}
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-28 bg-ink text-ivory p-7 md:p-8" data-testid="rc-summary">
            <div className="overline text-ivory/60">{cfg.summary.overline}</div>
            <h3 className="font-display text-2xl mt-3 font-light">{propertyName.trim() || "Your property"}</h3>
            {ptype && <div className="text-xs text-ivory/50 mt-1">{TYPES.find((t) => t.id === ptype)?.label}</div>}

            <div className="mt-6 space-y-3">
              <SumRow muted label={`Capacity · ${num(calc.capacity)} guests/night`} value={`${inr(calc.avgTicket)}/head`} />
              {N(propSize) > 0 && <SumRow muted label={`${propSize} ${propUnit} · ${num(N(eventSqft))} sqft event area · ${num(N(pools))} ${N(pools) === 1 ? "pool" : "pools"}`} value="" testId="rc-sum-property" />}
              {leisureOn && <SumRow label="Leisure & corporate" value={inr(calc.leisureRevenue)} testId="rc-sum-leisure" />}
              {weddingsOn && <SumRow label={`Weddings · ${num(N(weddings))}`} value={inr(calc.weddingRevenue)} testId="rc-sum-weddings" />}
              {socialsOn && spaces.length > 0 && <SumRow label="Private socials" value={inr(calc.socialsRevenue)} testId="rc-sum-socials" />}
            </div>

            <div className="mt-6 border-t border-ivory/15 pt-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-clay font-medium">{cfg.summary.total_label}</div>
              <div className="font-display text-4xl mt-2" data-testid="rc-total">{inr(calc.total)}</div>
              {leisureOn && <div className="text-xs text-ivory/50 mt-1">Minimum guarantee alone: {inr(calc.minRevenue)}</div>}
            </div>

            <div className="mt-6">
              <div className="flex justify-between text-xs text-ivory/60 mb-1.5">
                <span>Calendar days committed</span>
                <span data-testid="rc-days-committed">{num(calc.daysCommitted)} / 365</span>
              </div>
              <div className="h-1.5 bg-ivory/15 overflow-hidden rounded-full">
                <div className={`h-full transition-all ${overbooked ? "bg-red-400" : "bg-clay"}`} style={{ width: `${daysPct}%` }} />
              </div>
              {overbooked && (
                <div className="mt-2.5 flex items-start gap-2 text-[11px] text-amber-300" data-testid="rc-days-warning">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Your streams add up to more days than the year has — the same dates are being counted twice. Ease back a target for a realistic picture.
                </div>
              )}
            </div>

            <button type="button" onClick={requestDownload} disabled={downloading}
              className="mt-7 w-full bg-ivory text-ink px-6 py-4 text-xs uppercase tracking-[0.22em] font-medium inline-flex items-center justify-center gap-2 hover:bg-clay hover:text-white transition-colors disabled:opacity-60"
              data-testid="rc-download-pdf">
              <FileDown className="w-4 h-4" /> {downloading ? "Preparing PDF…" : cfg.summary.download_label}
            </button>
            <Link to="/partner-with-us#enquire"
              className="mt-3 w-full border border-ivory/30 text-ivory px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-medium inline-flex items-center justify-center gap-2 hover:bg-ivory/10 transition-colors"
              data-testid="rc-partner-link">
              <Handshake className="w-4 h-4" /> {cfg.summary.discuss_label}
            </Link>
            <p className="mt-4 text-[10px] text-ivory/40 leading-relaxed">{cfg.summary.disclaimer}</p>
          </aside>
        </div>
      </div>

      {/* Compare Partnership Models */}
      {ptype && calc.total > 0 && (
        <PartnershipCompare
          r={calc.total}
          propertyName={propertyName}
          propertyType={ptype}
          rooms={N(rooms)}
          stage={stage}
        />
      )}

      {/* Readiness cross-link */}
      {ptype && calc.total > 0 && (
        <section className="bg-clay/10 border-y border-clay/20" data-testid="rc-readiness-banner">
          <div className="container-x py-10 flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-start gap-4 max-w-2xl">
              <ClipboardCheck className="w-6 h-6 text-clay shrink-0 mt-1" />
              <div>
                <div className="overline text-clay">Next step</div>
                <h3 className="font-display text-xl md:text-2xl font-light mt-1.5">Is your property ready to deliver these numbers?</h3>
                <p className="text-sm text-graphite mt-2">Our structured readiness assessment covers 29 areas — legal approvals, safety, rooms, kitchens and event spaces — so you know exactly what's ready, what's missing and who funds each gap before we partner.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/property-readiness" className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] font-medium text-ink border border-ink px-5 py-2.5 hover:bg-ink hover:text-ivory transition-colors" data-testid="rc-readiness-link">
                Check property readiness <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to={`/staffing-calculator?r=${Math.round(calc.total)}${propertyName ? `&name=${encodeURIComponent(propertyName)}` : ""}`} className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] font-medium text-graphite border border-ink/30 px-5 py-2.5 hover:border-ink hover:text-ink transition-colors" data-testid="rc-staffing-link">
                Estimate staffing costs <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Closing strip */}
      {cfg.closing.enabled !== false && (
        <section className="bg-bone">
          <div className="container-x py-12 md:py-16 flex flex-wrap items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-light">{cfg.closing.heading}</h3>
              <p className="text-sm text-graphite mt-2 max-w-lg">{cfg.closing.text}</p>
            </div>
            <Link to="/partner-with-us" className="btn-primary inline-flex items-center gap-2" data-testid="rc-cta-partner">
              {cfg.closing.cta_label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      <GuestDetailsDialog
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        onSubmit={(g) => { setGuestOpen(false); downloadPdf(g); }}
        heading="Almost there — who is this projection for?"
        ctaLabel="Get my projection"
      />
    </div>
  );
}

export default function RevenueCalculator() {
  const { content, loading } = usePageContent("revenue_calculator");
  if (loading) return <div className="pt-44 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;
  return <CalculatorBody cfg={mergeRC(content)} />;
}
